import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
// Tip se uvozi pod drugim imenom: komponenta u ovom fajlu zove se isto, a dva
// `CooperationSnapshot` u istom dosegu su greška koja je obarala CI.
import { cooperationApi, type CooperationSnapshot as SnapshotData, type CooperationUnit } from "../api/cooperation";
import { Select } from "./Select";
import { useChartPalette } from "../theme/useChartPalette";
import { formatMoney, formatNumber } from "../lib/format";
import { useAuth } from "../auth/AuthContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Cooperation snapshot. By default it reads the caller's own company (auth
 * scope on the BE). When `companyId` is set, it scopes the stats to that
 * company — used by the Sales "Customer view" tab to preview a customer's
 * cooperation without impersonation.
 */
export function CooperationSnapshot({ companyId }: { companyId?: string } = {}) {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  // Narrow phones give the category axis less width so the bars aren't crushed by long labels.
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 820px)");
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Na kontrolnoj tabli kupca prop ne dolazi, pa se uzima njegova sopstvena firma. Interno lice
  // nema firmu i njemu ovaj pregled ni ne znači ništa, pa se ne crta.
  const { state, hasPermission } = useAuth();
  const canRead = hasPermission("section:orders:read") && hasPermission("section:customer_finance:read");
  const firma = companyId
    ?? (state.status === "authenticated" ? (state.user.companyId ?? undefined) : undefined);

  useEffect(() => {
    if (!firma || !canRead) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    cooperationApi
      .snapshot(firma, year, compareEnabled ? year - 1 : undefined)
      .then((r) => { if (!cancelled) setSnapshot(r); })
      .catch((e) => { if (!cancelled) setError(e.message ?? t("coop.error")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, compareEnabled, firma, t, canRead]);

  const view = useMemo<View>(
    () => buildView(snapshot, t("coop.chart.byLine.rest", "Ostalo i računi bez stavki")),
    [snapshot, t],
  );

  if (!firma || !canRead) return null;

  // The tile carries kilograms; what was invoiced in another unit is named under it, never added.
  // An empty unit is unknown, not a kilogram, and says so.
  const vanKilograma = view.otherUnits
    .filter((u) => u.quantity !== 0)
    .map((u) => `+ ${formatNumber(u.quantity, undefined, { maximumFractionDigits: 2 })} ` +
      (u.unit?.trim()
        ? t(`report.prod.unit.${u.unit.trim().toLowerCase()}`, u.unit.trim().toLowerCase())
        : t("report.prod.unit.unknown", "jedinica nepoznata")))
    .join(" · ");

  return (
    <section style={card}>
      <header style={head}>
        <div>
          <h2 style={title}>
            {t("coop.title")}
          </h2>
          <p style={subtitle}>
            <Trans
              i18nKey="coop.subtitle"
              values={{
                year,
                compareSuffix: compareEnabled ? t("coop.compareSuffix", { prevYear: year - 1 }) : "",
              }}
              components={{ 1: <strong /> }}
            />
          </p>
        </div>
        <div style={controls}>
          <Select<string>
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            style={{ width: 110 }}
            options={[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map((y) => ({
              value: String(y),
              label: String(y),
            }))}
          />
          <label style={toggle}>
            <input
              type="checkbox"
              checked={compareEnabled}
              onChange={(e) => setCompareEnabled(e.target.checked)}
            />
            {t("coop.compareWith", { prevYear: year - 1 })}
          </label>
        </div>
      </header>

      {error && <div style={errorBox}>{error}</div>}
      {loading && !snapshot && <p style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("coop.loading")}</p>}

      <div style={kpiRow}>
        <Kpi label={t("coop.kpi.orders", { year })} value={view.totalOrders.toString()} delta={view.deltaPct} />
        <Kpi label={t("coop.kpi.units")} value={formatInt(view.totalUnits)} note={vanKilograma || undefined} />
        {/* Invoices only: the opening balance and the other postings are debits too, but on a
            screen of what the customer bought they would show the first of January as a month of
            sales, so they are named under the value instead. */}
        <Kpi label={t("coop.kpi.revenue")} value={formatEur(view.totalEur)}
             hint={t("coop.kpi.hint.invoiced", "Računi za robu i usluge, sa PDV-om, bez prenosa iz ranijih godina")}
             note={view.carriedEur > 0 || view.otherEur > 0
               ? t("finance.notInvoiced", "Nije uračunato: preneto iz ranijih godina {{prenos}}, ostala knjiženja {{ostalo}}",
                 { prenos: formatEur(view.carriedEur), ostalo: formatEur(view.otherEur) })
               : undefined} />
        <Kpi
          label={t("coop.kpi.outstanding")}
          value={formatEur(view.outstandingEur)}
          accent={view.outstandingEur > 0 ? "warn" : "ok"}
          hint={t("coop.kpi.hint.outstanding")}
        />
      </div>

      <div style={chartBox}>
        <h3 style={chartTitle}>{t("coop.chart.unitsEur.title")}</h3>
        {/* Dve serije, dva različita dokumenta i dva različita datuma. Bez ove rečenice grafikon
            se čita kao „u julu malo robe za mnogo para", a zapravo znači da je ono što je
            naručeno u junu fakturisano u julu. */}
        <p style={chartNote}>{t("coop.chart.unitsEur.note")}</p>
        <div style={{ height: 320 }}>
          <ResponsiveContainer>
            <ComposedChart data={view.monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="units"
                orientation="left"
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                tickFormatter={(v) => formatUnitsAxis(v)}
                label={{ value: t("coop.chart.axis.units"), angle: -90, position: "insideLeft", offset: 10, style: { fontSize: 10, fill: palette.inkMuted } }}
              />
              <YAxis
                yAxisId="eur"
                orientation="right"
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatEurAxis(v)}
                label={{ value: t("coop.chart.axis.eur"), angle: 90, position: "insideRight", offset: 10, style: { fontSize: 10, fill: palette.inkMuted } }}
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8, fontSize: 12 }}
                cursor={{ fill: palette.hover }}
                formatter={(value, name, item) => vrednostOblacica(value, name, item?.dataKey)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="units" dataKey="actualUnits" name={t("coop.chart.series.actualUnits")} fill={palette.brandInk} radius={[4, 4, 0, 0]} />
              {compareEnabled && (
                <Bar yAxisId="units" dataKey="prevYearUnits" name={t("coop.chart.series.prevYearUnits", { year: year - 1 })} fill={palette.brand} radius={[4, 4, 0, 0]} />
              )}
              <Line
                yAxisId="eur"
                type="monotone"
                dataKey="actualEur"
                name={t("coop.chart.series.actualEur")}
                stroke={palette.ok}
                strokeWidth={2.5}
                dot={{ fill: palette.ok, r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={chartBox}>
        <h3 style={chartTitle}>{t("coop.chart.byLine.title")}</h3>
        <div style={{ height: 30 + view.byLine.length * 36 }}>
          <ResponsiveContainer>
            <ComposedChart data={view.byLine} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
              {/* Ista osa nosi i jedinice i evre, pa se skraćuje bez oznake valute. */}
              <XAxis type="number" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false}
                     allowDecimals={false} tickFormatter={(v) => formatUnitsAxis(v)} />
              <YAxis
                type="category"
                dataKey="line"
                tick={{ fontSize: 12, fill: palette.inkStrong }}
                axisLine={false}
                tickLine={false}
                width={narrow ? 96 : 170}
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8, fontSize: 12 }}
                cursor={{ fill: palette.hover }}
                formatter={(value, name, item) => vrednostOblacica(value, name, item?.dataKey)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="units" name={t("coop.chart.series.units")} fill={palette.brandInk} radius={[0, 4, 4, 0]} />
              <Bar dataKey="eur" name={t("coop.chart.series.revenueEur")} fill={palette.ok} radius={[0, 4, 4, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

/**
 * The tooltip value of a series, formatted by what the series holds.
 *
 * The format is chosen by the data key and never by the series name: the name is a translation,
 * and a name that happens to carry no currency code would print an amount in euros as a bare count.
 */
export function vrednostOblacica(value: unknown, name: unknown, dataKey: unknown): [string, string] {
  const n = String(name);
  // A month after the day the invoices reach has no figure: a dash, never a zero.
  if (value == null) return ["—", n];
  const v = Number(value);
  return dataKey === "actualEur" || dataKey === "eur" ? [formatEur(v), n] : [formatInt(v), n];
}

function Kpi({
  label,
  value,
  delta,
  accent,
  hint,
  note,
}: {
  label: string;
  value: string;
  delta?: number | null;
  accent?: "ok" | "warn" | "err";
  hint?: string;
  /** What the value leaves out, under the hint. */
  note?: string;
}) {
  const { t } = useTranslation();
  const positive = (delta ?? 0) >= 0;
  const accentColor =
    accent === "ok" ? "var(--ok)" : accent === "warn" ? "var(--warn)" : accent === "err" ? "var(--err)" : undefined;
  return (
    <div style={kpiTile}>
      <span style={kpiLabel}>{label}</span>
      <strong style={{ ...kpiValue, ...(accentColor ? { color: accentColor } : {}) }}>{value}</strong>
      {delta !== undefined && delta !== null && (
        <span style={{ fontSize: 12, color: positive ? "var(--ok)" : "var(--err)" }}>
          {positive ? "▲" : "▼"} {t("coop.kpi.deltaVsPrev", { delta: Math.abs(delta) })}
        </span>
      )}
      {hint && <span style={kpiHint}>{hint}</span>}
      {note && <span style={kpiNote}>{note}</span>}
    </div>
  );
}

type MonthlyRow = {
  month: string;
  /** Null for a month the data does not reach yet; the chart leaves it empty. */
  actualUnits: number | null;
  prevYearUnits?: number;
  actualEur: number | null;
};

type LineRow = { line: string; units: number; eur: number };

type View = {
  monthly: MonthlyRow[];
  byLine: LineRow[];
  totalOrders: number;
  totalUnits: number;
  otherUnits: CooperationUnit[];
  totalEur: number;
  carriedEur: number;
  otherEur: number;
  outstandingEur: number;
  deltaPct: number | null;
};

/**
 * Server već računa sve; ovde se samo prepakuje u oblik koji grafikoni očekuju.
 *
 * The remainder row comes without a name, and gets the one given here, in the reader's language.
 */
function buildView(s: SnapshotData | null, ostatak: string): View {
  const meseci = s?.monthly ?? [];
  const monthly: MonthlyRow[] = MONTHS.map((m, i) => {
    const red = meseci.find((x) => x.month === i + 1);
    // A month the server sent without figures stays without them; a month it did not send is zero.
    return {
      month: m,
      actualUnits: red ? red.units : 0,
      actualEur: red ? red.invoicedEur : 0,
      ...(red?.prevYearUnits != null ? { prevYearUnits: red.prevYearUnits } : {}),
    };
  });
  return {
    monthly,
    byLine: (s?.byLine ?? []).map((l) => ({ line: l.line ?? ostatak, units: l.units, eur: l.eur })),
    totalOrders: s?.orders ?? 0,
    totalUnits: s?.units ?? 0,
    otherUnits: s?.otherUnits ?? [],
    totalEur: s?.invoicedEur ?? 0,
    carriedEur: s?.carriedPostedEur ?? 0,
    otherEur: s?.otherPostedEur ?? 0,
    outstandingEur: s?.outstandingEur ?? 0,
    deltaPct: s?.ordersDeltaPct ?? null,
  };
}

function formatInt(n: number): string {
  return formatNumber(n, undefined, { maximumFractionDigits: 0 });
}
function formatEur(n: number): string {
  return formatMoney(n, "EUR", undefined, { maximumFractionDigits: 0 });
}
function formatEurAxis(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M €`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k €`;
  return `${n} €`;
}
/**
 * Količine na osi, skraćeno.
 *
 * Dok su stupci dolazili sa porudžbina, osa je išla do nekoliko jedinica i sirov broj je stajao
 * lepo. Sa stavki računa ide do stotina hiljada, pa bi „165400" na svakoj crtici zauzeo više
 * mesta nego sam grafikon.
 */
function formatUnitsAxis(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  padding: 20,
  borderRadius: 12,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const head: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: 12,
};
const title: React.CSSProperties = { margin: 0, fontSize: 16, color: "var(--brand-ink)" };
const subtitle: React.CSSProperties = { margin: "2px 0 0", fontSize: 12, color: "var(--ink-3)" };
const controls: React.CSSProperties = { display: "flex", gap: 12, alignItems: "center" };
const toggle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink)" };
const kpiRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
};
const kpiTile: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: 14,
  background: "linear-gradient(135deg, var(--surface-2), var(--surface-muted))",
  borderRadius: 10,
  border: "1px solid var(--border)",
};
const kpiLabel: React.CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const kpiValue: React.CSSProperties = { fontSize: 22, color: "var(--brand-ink)" };
// A word wider than the tile breaks inside the tile instead of running under the next one.
const kpiHint: React.CSSProperties = {
  fontSize: 10, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, overflowWrap: "anywhere",
};
const kpiNote: React.CSSProperties = { fontSize: 11, lineHeight: 1.35, color: "var(--ink-3)", overflowWrap: "anywhere" };
const chartBox: React.CSSProperties = {
  background: "var(--surface-2)",
  borderRadius: 10,
  padding: 12,
  border: "1px solid var(--border)",
};
const chartNote: React.CSSProperties = {
  margin: "0 0 10px", fontSize: 12, color: "var(--ink-3)", lineHeight: 1.45,
};
const chartTitle: React.CSSProperties = { margin: "0 0 8px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
